import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { startRun } from "@/api/runs";
import { generationErrorMessage } from "@/api/llm-access";
import { uploadSourcePdf } from "@/api/lecture";
import { savePdf } from "./lecture-pdf-store";
import { extractPdfText } from "./extract-pdf-text";
import { snapshotFile } from "../snapshot-file";
import type { StartInputs } from "@/api/create-types";
import { Alert, Button, Input, Spinner } from "@/components/ui";

// Learning-goal presets (E.3b). Calibrate the teacher discussion's coverage,
// depth and question mix; mirrors backend lecture._LG_PRESET_CALIBRATION keys.
const LG_PRESETS: { key: string; label: string }[] = [
  { key: "exam", label: "Exam prep" },
  { key: "deep", label: "Deep understanding" },
  { key: "practical", label: "Practical application" },
  { key: "quick", label: "Quick review" },
];

// PDF-first start for the `lecture` framework. Unlike the generic setup-form,
// lecture needs the original PDF (not just its text): we stash the blob in
// IndexedDB under a ref, extract its text for source_text, and start the run.
// The run pauses at lecture.marking; the run page renders the PDF for marking.
export function LectureStart() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState("");
  const [title, setTitle] = useState("");
  const [sourceText, setSourceText] = useState("");
  const [chars, setChars] = useState(0);
  const [parts, setParts] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [pdfRef, setPdfRef] = useState<string | null>(null);
  const [sourcePdfKey, setSourcePdfKey] = useState<string | null>(null);
  const [lgPreset, setLgPreset] = useState<string | null>(null);
  const [lgNote, setLgNote] = useState("");
  const [processing, setProcessing] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onPick = async (f: File) => {
    setError(null);
    setProcessing(true);
    setParts(null);
    try {
      setFileName(f.name);
      if (!title.trim()) setTitle(f.name.replace(/\.pdf$/i, ""));
      // Snapshot the bytes once so the stash / upload / extract below all read a
      // stable copy (the original File can read empty after the input resets).
      const stable = await snapshotFile(f);
      const ref = await savePdf(stable);
      setPdfRef(ref);
      // Persist the source PDF server-side in the background (for the Library
      // download + inline view). Non-blocking: failure just means the produced
      // set won't carry a source, never blocks marking/generation.
      setSourcePdfKey(null);
      void uploadSourcePdf(stable)
        .then((r) => setSourcePdfKey(r.key))
        .catch(() => setSourcePdfKey(null));
      const text = await extractPdfText(stable, (m, partCount) => {
        setNote(m);
        if (partCount) setParts(partCount);
      });
      setSourceText(text);
      setChars(text.length);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Couldn't read that PDF."));
      setPdfRef(null);
      setSourceText("");
    } finally {
      setProcessing(false);
      setNote("");
    }
  };

  // PDF saved + done processing is enough — scanned PDFs may extract no text
  // and are still markable visually (the marks drive generation).
  const ready = !!pdfRef && !processing;

  const start = async () => {
    if (!pdfRef) return;
    setStarting(true);
    setError(null);
    try {
      const inputs: StartInputs = {
        language: "en",
        source_text: sourceText,
        local_pdf_ref: pdfRef,
        discussion: true,
      };
      if (title.trim()) inputs.title = title.trim();
      if (sourcePdfKey) inputs.source_pdf_key = sourcePdfKey;
      if (lgPreset || lgNote.trim()) {
        inputs.learning_goals = { preset: lgPreset, note: lgNote.trim() || null };
      }
      const run = await startRun("lecture", inputs);
      navigate(`/app/create/run/${run.id}`);
    } catch (e) {
      setError(generationErrorMessage(e, t));
      setStarting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-ink-soft">{t("Your lecture or paper")}</h3>
        <p className="mt-1 text-sm text-ink-faint">
          {t("Open the PDF, read it, and mark what to learn. Eddi turns your marks into a full study unit — definitions to memorise, derivations walked through, and the connecting topics that tie it together.")}
        </p>

        <input
          ref={fileInput}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onPick(f);
          }}
        />

        <div className="mt-4 flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => fileInput.current?.click()}
            disabled={processing || starting}
          >
            {fileName ? t("Choose a different PDF") : t("Choose a PDF")}
          </Button>
          {processing && (
            <span className="flex items-center gap-2 text-sm text-ink-faint">
              <Spinner className="h-4 w-4" /> {note || t("Reading the PDF…")}
            </span>
          )}
        </div>

        {fileName && !processing && (
          <p className="mt-3 text-sm text-ink-faint">
            📄 {fileName}
            {parts && parts > 1 ? <> · {t("split into {{count}} parts for upload", { count: parts })}</> : null}
            {chars > 0 ? (
              <> · {t("{{count}} characters extracted", { count: chars })}</>
            ) : (
              <> · {t("no text — mark it visually")}</>
            )}
          </p>
        )}
      </div>

      <div className="card p-5">
        <Input
          label={t("Set name (optional)")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("Auto-named if blank")}
        />
      </div>

      <div className="card p-5">
        <h3 className="text-sm font-semibold text-ink-soft">{t("Your goal (optional)")}</h3>
        <p className="mt-1 text-sm text-ink-faint">
          {t("Tell Eddi what you're studying for — it tunes the coverage, depth and question mix to match.")}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {LG_PRESETS.map((p) => {
            const active = lgPreset === p.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setLgPreset(active ? null : p.key)}
                className={
                  "rounded-full border px-3 py-1.5 text-sm transition " +
                  (active
                    ? "border-brand-500 bg-brand-50 text-brand-700"
                    : "border-black/10 text-ink-soft hover:border-black/20")
                }
              >
                {t(p.label)}
              </button>
            );
          })}
        </div>
        <textarea
          value={lgNote}
          onChange={(e) => setLgNote(e.target.value)}
          rows={2}
          maxLength={300}
          placeholder={t("Anything specific? e.g. “focus on the proofs for my oral exam”")}
          className="mt-3 w-full rounded-lg border border-black/10 bg-white p-2.5 text-sm text-ink placeholder:text-ink-faint focus:border-brand-400 focus:outline-none"
        />
      </div>

      {error && <Alert>{error}</Alert>}

      <Button size="lg" onClick={start} loading={starting} disabled={!ready || starting}>
        {t("Open & mark the PDF")}
      </Button>
    </div>
  );
}
